import type { Filter } from '../components/TableFilter';
import type { MockContact } from '../data/mockContacts';
import { getCadence } from './contactInsights';

type FilterValues = Record<string, string[]>;

const DAY_MS = 24 * 60 * 60 * 1000;

export const STAFF_FOR_TASKS = ['Chris', 'Jude', 'Eranna', 'Sandeep'];

const option = (value: string, label: string) => ({
  id: value,
  label,
  value
});

const daysSince = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((now.getTime() - new Date(year, month - 1, day).getTime()) / DAY_MS);
};

// Number at the end of option values such as "n2" or "w4"
const numberIn = (value: string) => Number(value.replace(/\D/g, ''));

// Filters for the call-prep columns. Multi-select options match if any one matches.
export function buildInsightFilters(values: FilterValues, open: Set<string>): Filter[] {
  const multi = (id: string, label: string, options: ReturnType<typeof option>[]): Filter => ({
    id,
    label,
    options,
    selectedValues: values[id] || [],
    multiSelect: true,
    isOpen: open.has(id)
  });
  const single = (id: string, label: string, options: ReturnType<typeof option>[]): Filter => ({
    id,
    label,
    options,
    selectedValue: values[id]?.[0] || '',
    isOpen: open.has(id)
  });
  const search = (id: string, label: string): Filter => ({
    id,
    label,
    options: [],
    selectedValue: values[id]?.[0] || '',
    type: 'search',
    isOpen: open.has(id)
  });
  return [
  // Outreach
  multi('callFlag', 'Call flag', [option('top-customer', 'Top customer'), option('dont-call', "Don't call"), option('not-worth-chasing', 'Not worth chasing'), option('none', 'No flag'), option('exclude-dont-call', "Exclude Don't call")]), multi('lastContacted', 'Last contacted', [option('never', 'Never'), option('not-30', 'Not in last 30 days'), option('not-60', 'Not in last 60 days'), option('not-90', 'Not in last 90 days')]), multi('openTasks', 'Open tasks', [option('yes', 'Has open tasks'), option('no', 'No open tasks'), ...STAFF_FOR_TASKS.map(name => option(`assigned:${name}`, `Assigned to ${name}`))]), single('hasPhone', 'Has phone number', [option('yes', 'Yes'), option('no', 'No')]),
  // Buying behaviour
  multi('cadenceStatus', 'Cadence status', [option('on-track', 'On track'), option('due-soon', 'Due soon'), option('lapsed', 'Lapsed')]), single('overdueBy', 'Overdue by more than', [option('w2', '2 weeks'), option('w4', '4 weeks'), option('w8', '8 weeks'), option('w12', '12 weeks')]), single('spendTrend', 'Spending trend', [option('up', 'Up'), option('down', 'Down'), option('up-10', 'Up by more than 10%'), option('up-20', 'Up by more than 20%'), option('down-10', 'Down by more than 10%'), option('down-20', 'Down by more than 20%'), option('down-30', 'Down by more than 30%')]), single('skipsPauses', 'Skips & pauses (12m)', [option('n1', 'At least 1'), option('n2', 'At least 2'), option('n3', 'At least 3'), option('n5', 'At least 5')]), multi('buyerType', 'Buyer type', [option('club', 'Club only'), option('one-time', 'One-time only'), option('both', 'Both')]), single('oneTimeOrders', 'One-time orders', [option('n1', 'At least 1'), option('n2', 'At least 2'), option('n3', 'At least 3'), option('n5', 'At least 5')]), multi('lastOrderRange', 'Last order date', [option('d30', 'Last 30 days'), option('d90', 'Last 90 days'), option('m3-6', '3–6 months ago'), option('m6plus', 'Over 6 months ago')]), search('productBought', 'Product bought'), search('topProduct', 'Top product'),
  // Membership
  single('paymentProblem', 'Payment problem', [option('yes', 'Yes'), option('no', 'No')])];
}

export function matchesInsightFilters(contact: MockContact, values: FilterValues): boolean {
  const insights = contact.insights;
  const picked = (id: string) => values[id]?.filter(Boolean) ?? [];

  const flags = picked('callFlag');
  if (flags.length) {
    const positive = flags.filter(flag => flag !== 'exclude-dont-call');
    if (positive.length && !positive.includes(insights.callFlag)) return false;
    if (flags.includes('exclude-dont-call') && insights.callFlag === 'dont-call') return false;
  }

  const contacted = picked('lastContacted');
  if (contacted.length) {
    const since = insights.lastContacted ? daysSince(insights.lastContacted.date) : null;
    const ok = contacted.some(value => value === 'never' ? since === null : since === null || since > numberIn(value));
    if (!ok) return false;
  }

  const tasks = picked('openTasks');
  if (tasks.length) {
    const ok = tasks.some(value => {
      if (value === 'yes') return insights.openTaskAssignees.length > 0;
      if (value === 'no') return insights.openTaskAssignees.length === 0;
      return insights.openTaskAssignees.includes(value.replace('assigned:', ''));
    });
    if (!ok) return false;
  }

  const phone = picked('hasPhone')[0];
  if (phone && phone === 'yes' !== contact.phone.trim().length > 0) return false;

  const cadence = getCadence(insights);
  const statuses = picked('cadenceStatus');
  if (statuses.length && (!cadence || !statuses.includes(cadence.status))) return false;
  const overdue = picked('overdueBy')[0];
  if (overdue && (!cadence || cadence.status !== 'lapsed' || cadence.days <= numberIn(overdue) * 7)) return false;

  const trend = picked('spendTrend')[0];
  if (trend) {
    const pct = insights.spendTrendPct;
    const threshold = numberIn(trend);
    const ok = trend.startsWith('up') ? pct > threshold || threshold === 0 && pct > 0 : pct < -threshold || threshold === 0 && pct < 0;
    if (!ok) return false;
  }

  const skips = picked('skipsPauses')[0];
  if (skips && insights.skips + insights.pauses < numberIn(skips)) return false;

  const buyer = picked('buyerType');
  if (buyer.length) {
    const type = insights.clubOrders > 0 && insights.oneTimeOrders > 0 ? 'both' : insights.clubOrders > 0 ? 'club' : 'one-time';
    if (!buyer.includes(type)) return false;
  }

  const oneTime = picked('oneTimeOrders')[0];
  if (oneTime && insights.oneTimeOrders < numberIn(oneTime)) return false;

  const lastOrder = picked('lastOrderRange');
  if (lastOrder.length) {
    const since = daysSince(contact.lastOrder);
    const ok = lastOrder.some(value => {
      if (value === 'd30') return since <= 30;
      if (value === 'd90') return since <= 90;
      if (value === 'm3-6') return since > 90 && since <= 182;
      return since > 182;
    });
    if (!ok) return false;
  }

  const product = picked('productBought')[0]?.trim().toLowerCase();
  if (product && !insights.productsPurchased.some(name => name.toLowerCase().includes(product))) return false;
  const top = picked('topProduct')[0]?.trim().toLowerCase();
  if (top && !insights.topProduct.toLowerCase().includes(top)) return false;

  const payment = picked('paymentProblem')[0];
  if (payment && payment === 'yes' !== (contact.paymentStatus === 'Failed')) return false;

  return true;
}

// Headings for the Add Filter menu, in display order
const groups: [string, string[]][] = [['Outreach', ['callFlag', 'lastContacted', 'openTasks', 'hasPhone']], ['Buying behaviour', ['cadenceStatus', 'overdueBy', 'spendTrend', 'skipsPauses', 'buyerType', 'oneTimeOrders', 'lastOrderRange', 'productBought', 'topProduct']], ['Value', ['ltv', 'totalOrders', 'aov']], ['Membership', ['subscription', 'status', 'paymentProblem', 'paymentStatus', 'tags', 'memberSince', 'nextBilling']], ['Contact details', ['contact', 'phone', 'address', 'source', 'note']]];

const relabel: Record<string, string> = {
  subscription: 'Club',
  status: 'Subscription status',
  paymentStatus: 'Payment status',
  tags: 'Tags',
  ltv: 'LTV',
  totalOrders: 'Total orders'
};

// Puts every filter under its heading; filters not listed are dropped
export function groupContactFilters(filters: Filter[]): Filter[] {
  return groups.flatMap(([heading, ids]) => ids.map(id => filters.find(filter => filter.id === id)).filter((filter): filter is Filter => !!filter).map(filter => ({
    ...filter,
    label: relabel[filter.id] ?? filter.label,
    group: heading
  })));
}
